import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import BrandLogo from '@/components/landing/brand-logo'
import NeonLink from '@/components/landing/neon-link'
import { NAV_LINKS } from '@/constants/landing'

interface SiteHeaderProps {
    showCta?: boolean
}

const SiteHeader: FC<SiteHeaderProps> = ({ showCta = true }): ReactNode => {
    const [scrolled, setScrolled] = useState(false)
    const [open, setOpen] = useState(false)

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 12)

        handleScroll()
        window.addEventListener('scroll', handleScroll, { passive: true })

        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    useEffect(() => {
        if (!open) return

        const handleKey = (event: globalThis.KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false)
        }

        const handleResize = () => {
            if (window.innerWidth >= 1024) setOpen(false)
        }

        window.addEventListener('keydown', handleKey)
        window.addEventListener('resize', handleResize)

        return () => {
            window.removeEventListener('keydown', handleKey)
            window.removeEventListener('resize', handleResize)
        }
    }, [open])

    return (
        <header
            className={cn(
                'fixed inset-x-0 top-0 z-50 border-b transition-colors duration-300',
                scrolled || open ? 'border-border bg-background/80 backdrop-blur-xl' : 'border-transparent bg-transparent'
            )}
        >
            <nav aria-label='Main' className='mx-auto flex h-16 max-w-7xl items-center justify-between gap-6 px-5 sm:px-8'>
                <BrandLogo />

                <ul className='hidden items-center gap-0.5 lg:flex'>
                    {NAV_LINKS.map(link => (
                        <li key={link.href}>
                            <a
                                href={link.href}
                                className='rounded-full px-3.5 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-white/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neon-cyan'
                            >
                                {link.label}
                            </a>
                        </li>
                    ))}
                </ul>

                <div className='flex items-center gap-2'>
                    {showCta && (
                        <NeonLink href='/#play' size='sm' className='hidden sm:inline-flex'>
                            Play now
                        </NeonLink>
                    )}

                    <button
                        type='button'
                        aria-expanded={open}
                        aria-controls='mobile-menu'
                        aria-label={open ? 'Close menu' : 'Open menu'}
                        onClick={() => setOpen(value => !value)}
                        className='inline-flex size-10 items-center justify-center rounded-full border border-border text-foreground transition-colors hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neon-cyan lg:hidden'
                    >
                        {open ? <X className='size-5' /> : <Menu className='size-5' />}
                    </button>
                </div>
            </nav>

            {open && (
                <div id='mobile-menu' className='border-t border-border lg:hidden'>
                    <ul className='mx-auto flex max-w-7xl flex-col gap-1 px-5 py-4 sm:px-8'>
                        {NAV_LINKS.map(link => (
                            <li key={link.href}>
                                <a
                                    href={link.href}
                                    onClick={() => setOpen(false)}
                                    className='block rounded-xl px-3 py-3 text-base font-medium text-foreground/90 transition-colors hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neon-cyan'
                                >
                                    {link.label}
                                </a>
                            </li>
                        ))}

                        {showCta && (
                            <li className='pt-3'>
                                <NeonLink href='/#play' onClick={() => setOpen(false)} className='w-full'>
                                    Play now
                                </NeonLink>
                            </li>
                        )}
                    </ul>
                </div>
            )}
        </header>
    )
}

export default SiteHeader
